/**
 * Monthly commission statements (convention art. 3.4; docs/PLAN.md §9). After each calendar month
 * Wally publishes one statement per partner with the commission lines that count and are not held;
 * the partner invoices the total through Peppol and Wally pays undisputed amounts within 30 days
 * after the month's end. A published statement never changes: later differences are lines of a
 * later statement.
 */
import { z } from 'zod';
import { isoDay, objectIdSchema } from './common.js';
export const statementStatusSchema = z.enum(['draft', 'published', 'invoiced', 'paid']);
/** Wally, as named on statements (the party of the convention). */
export const WALLY_COMPANY = {
    legalName: 'agent.wally BV',
    address: ['Xavier De Cocklaan 49', '9831 Sint-Martens-Latem', 'Belgique / België'],
    companyNumber: 'BE1025254168',
};
/** `YYYY-MM` */
export const isoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'invalid month');
// Input
export const createStatementSchema = z
    .object({ partnerId: objectIdSchema, month: isoMonth })
    .strict();
export const markInvoicedSchema = z
    .object({ number: z.string().trim().min(1).max(60), date: isoDay })
    .strict();
export const markPaidSchema = z.object({ paidOn: isoDay }).strict();
/** Art. 3.4: a disputed line is held out of statements until released. */
export const holdLineSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
/** A correction of an error in a published statement: ± commission, with a reason (plan §8). */
export const createCorrectionSchema = z
    .object({
    partnerId: objectIdSchema,
    registrationId: objectIdSchema.nullable().default(null),
    /** Commission in cents, excl. VAT; negative takes back. */
    commission: z
        .number()
        .int()
        .refine((n) => n !== 0, 'not zero')
        .refine((n) => Math.abs(n) <= 100_000_00, 'too large'),
    reason: z.string().trim().min(5).max(500),
})
    .strict();
export const listStatementsQuerySchema = z.object({
    partnerId: objectIdSchema.optional(),
    status: statementStatusSchema.optional(),
});
/** Art. 3.4: Wally pays within 30 days after the end of the month. */
export function statementDueOn(month, invoiceDate = null) {
    const [y, m] = month.split('-').map(Number);
    const monthEnd = new Date(Date.UTC(y, m, 0));
    const due = new Date(monthEnd.getTime() + 30 * 86_400_000).toISOString().slice(0, 10);
    // An invoice received after that is paid on receipt.
    return invoiceDate && invoiceDate > due ? invoiceDate : due;
}
/** The month before `month` (`YYYY-MM`). */
export function previousMonth(month) {
    const [y, m] = month.split('-').map(Number);
    return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}
